import { useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import AddIcon from "@mui/icons-material/Add";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getData, getName } from "country-list";
import CustomTable from "../../components/CustomTable";
import { StyledTableCell } from "../../components/styled";
import {
  createWorldCupNation,
  setWorldCupNationStatus,
  updateWorldCupNation,
} from "../../services/worldCupNation.service";
import { getWorldCupNationsForTable } from "../../utils/worldCupNation";
import { normalizeFieldErrors } from "../../utils";
import { getFlagURIByCountryCode } from "../../utils/countrySearch";

const MAX_ISO_CODE_LENGTH = 2;
const MAX_SHORT_NAME_LENGTH = 10;
const MAX_NAME_LENGTH = 255;
const GROUP_OPTIONS = "ABCDEFGHIJKL".split("");
const FLAG_COL_WIDTH_PX = 80;
const FLAG_SIZE_PX = 32;

const COUNTRY_OPTIONS = getData().map(({ code, name }) => ({
  code: String(code || "").toUpperCase(),
  name: String(name || ""),
}));

const NationFlag = ({ code, name }) => {
  const [failed, setFailed] = useState(false);
  const flagUri = getFlagURIByCountryCode(code);

  if (!flagUri || failed) {
    return (
      <Box
        sx={{
          width: FLAG_SIZE_PX,
          height: FLAG_SIZE_PX,
          borderRadius: 1,
          bgcolor: "action.hover",
        }}
      />
    );
  }

  return (
    <Link
      href={flagUri}
      target="_blank"
      rel="noreferrer"
      aria-label={name ? `${name} flag` : "Nation flag"}
    >
      <img
        src={flagUri}
        alt=""
        onError={() => setFailed(true)}
        style={{
          display: "block",
          width: FLAG_SIZE_PX,
          height: FLAG_SIZE_PX,
          objectFit: "contain",
        }}
        loading="lazy"
      />
    </Link>
  );
};

const applyApiFieldErrors = (err, nextFieldErrors) => {
  const errorCode = err?.data?.error;
  const message = err?.message || "World Cup nation already exists";

  if (errorCode === "ALREADY_EXISTS" || /already exists/i.test(message)) {
    nextFieldErrors.shortName = message;
    if (/name/i.test(message)) {
      nextFieldErrors.name = message;
    }
  }

  return nextFieldErrors;
};

const WorldCupNations = () => {
  const queryClient = useQueryClient();
  const [sortOrder, setSortOrder] = useState("ASC");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [editingNation, setEditingNation] = useState(null);

  const [group, setGroup] = useState("");
  const [code, setCode] = useState("");
  const [shortName, setShortName] = useState("");
  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const nameManuallyEditedRef = useRef(false);

  const selectedCountry = useMemo(() => {
    const upper = code.trim().toUpperCase();
    if (!upper) {
      return null;
    }
    return COUNTRY_OPTIONS.find((c) => c.code === upper) || null;
  }, [code]);

  const resetForm = () => {
    setGroup("");
    setCode("");
    setShortName("");
    setName("");
    setFormError("");
    setFieldErrors({});
    nameManuallyEditedRef.current = false;
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingNation(null);
    resetForm();
  };

  const openCreate = () => {
    setMode("create");
    setEditingNation(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (nation) => {
    setMode("edit");
    setEditingNation(nation);
    setGroup(nation?.group ?? "");
    setCode(nation?.code ?? "");
    setShortName(nation?.shortName ?? "");
    setName(nation?.name ?? "");
    setFormError("");
    setFieldErrors({});
    nameManuallyEditedRef.current = true;
    setOpen(true);
  };

  const suggestNameFromIsoCode = (isoCode) => {
    const upper = String(isoCode || "")
      .trim()
      .toUpperCase();
    if (!upper || upper === "GB" || nameManuallyEditedRef.current) {
      return;
    }

    const suggestedName = getName(upper.toLowerCase());
    if (suggestedName) {
      setName(suggestedName);
    }
  };

  const handleIsoCodeChange = (nextCode) => {
    setCode(nextCode);
    if (fieldErrors?.code) {
      setFieldErrors((prev) => ({ ...prev, code: undefined }));
    }
  };

  const handleIsoCodeBlur = () => {
    const upper = code.trim().toUpperCase();
    setCode(upper);
    suggestNameFromIsoCode(upper);
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
  };

  const listQueryKey = useMemo(() => ["worldCupNations"], []);

  const { mutate: createNation, isPending: isCreatePending } = useMutation({
    mutationKey: ["createWorldCupNation"],
    mutationFn: (payload) => createWorldCupNation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(listQueryKey);
      queryClient.invalidateQueries({ queryKey: ["worldCupNationMap"] });
      closeDialog();
    },
    onError: (err) => {
      let nextFieldErrors = normalizeFieldErrors(err?.fieldErrors);
      nextFieldErrors = applyApiFieldErrors(err, nextFieldErrors);
      setFieldErrors(nextFieldErrors);
      setFormError(err?.message || "Failed to create nation");
    },
  });

  const { mutate: updateNation, isPending: isUpdatePending } = useMutation({
    mutationKey: ["updateWorldCupNation"],
    mutationFn: ({ id, payload }) => updateWorldCupNation(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(listQueryKey);
      queryClient.invalidateQueries({ queryKey: ["worldCupNationMap"] });
      closeDialog();
    },
    onError: (err) => {
      let nextFieldErrors = normalizeFieldErrors(err?.fieldErrors);
      nextFieldErrors = applyApiFieldErrors(err, nextFieldErrors);
      setFieldErrors(nextFieldErrors);
      setFormError(err?.message || "Failed to update nation");
    },
  });

  const { mutate: toggleNationStatus, isPending: isStatusPending } =
    useMutation({
      mutationKey: ["setWorldCupNationStatus"],
      mutationFn: ({ id, isActive }) => setWorldCupNationStatus(id, isActive),
      onSuccess: () => {
        queryClient.invalidateQueries(listQueryKey);
        queryClient.invalidateQueries({ queryKey: ["worldCupNationMap"] });
      },
      onError: (err) => {
        alert(err?.message || "Failed to update nation status");
      },
    });

  const isSaving = isCreatePending || isUpdatePending;

  const submit = () => {
    const trimmedGroup = group.trim().toUpperCase();
    const trimmedCode = code.trim().toUpperCase();
    const trimmedShortName = shortName.trim().toUpperCase();
    const trimmedName = name.trim();

    const nextFieldErrors = {};
    if (!trimmedGroup) {
      nextFieldErrors.group = "Group is required";
    } else if (
      trimmedGroup.length !== 1 ||
      !GROUP_OPTIONS.includes(trimmedGroup)
    ) {
      nextFieldErrors.group = "Group must be a single letter A–L";
    }

    if (!trimmedCode) {
      nextFieldErrors.code = "ISO code is required";
    } else if (trimmedCode.length !== MAX_ISO_CODE_LENGTH) {
      nextFieldErrors.code = "ISO code must be exactly 2 characters";
    } else if (!getName(trimmedCode.toLowerCase())) {
      nextFieldErrors.code = "ISO code must be a valid country code";
    }

    if (!trimmedShortName) {
      nextFieldErrors.shortName = "Short name is required";
    } else if (trimmedShortName.length > MAX_SHORT_NAME_LENGTH) {
      nextFieldErrors.shortName = `Short name must be ${MAX_SHORT_NAME_LENGTH} characters or less`;
    }

    if (trimmedName.length > MAX_NAME_LENGTH) {
      nextFieldErrors.name = `Name must be ${MAX_NAME_LENGTH} characters or less`;
    } else if (trimmedCode === "GB" && !trimmedName) {
      nextFieldErrors.name =
        "Name is required for United Kingdom nations (e.g. England, Scotland)";
    }

    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    setFormError("");
    setFieldErrors({});

    const payload = {
      group: trimmedGroup,
      code: trimmedCode,
      shortName: trimmedShortName,
    };
    if (trimmedName) {
      payload.name = trimmedName;
    }

    if (
      mode === "edit" &&
      editingNation?.id !== null &&
      editingNation?.id !== undefined
    ) {
      updateNation({ id: editingNation.id, payload });
      return;
    }

    createNation(payload);
  };

  const renderHeader = () => (
    <TableRow>
      <StyledTableCell
        sx={{
          width: FLAG_COL_WIDTH_PX,
          minWidth: FLAG_COL_WIDTH_PX,
          maxWidth: FLAG_COL_WIDTH_PX,
        }}
      >
        Flag
      </StyledTableCell>

      <StyledTableCell>
        <TableSortLabel
          active
          direction={sortOrder === "ASC" ? "asc" : "desc"}
          onClick={toggleSortOrder}
        >
          Group
        </TableSortLabel>
      </StyledTableCell>

      <StyledTableCell>Short name</StyledTableCell>

      <StyledTableCell>ISO code</StyledTableCell>

      <StyledTableCell>Name</StyledTableCell>

      <StyledTableCell>Status</StyledTableCell>

      <StyledTableCell>Actions</StyledTableCell>
    </TableRow>
  );

  const onToggleStatusClick = (nation) => {
    const nextActive = !nation.isActive;

    if (!nextActive) {
      const confirmed = window.confirm(
        `Deactivate ${nation.shortName} (${nation.name})? It will be hidden from the app nation picker. Existing users keep their selection.`,
      );
      if (!confirmed) {
        return;
      }
    }

    toggleNationStatus({ id: nation.id, isActive: nextActive });
  };

  const renderRow = (nation) => (
    <TableRow
      key={nation.id}
      sx={{
        opacity: nation.isActive === false ? 0.55 : 1,
      }}
    >
      <TableCell
        sx={{
          width: FLAG_COL_WIDTH_PX,
          minWidth: FLAG_COL_WIDTH_PX,
          maxWidth: FLAG_COL_WIDTH_PX,
        }}
      >
        <NationFlag code={nation.code} name={nation.name} />
      </TableCell>
      <TableCell>{nation.group}</TableCell>
      <TableCell>{nation.shortName}</TableCell>
      <TableCell>{nation.code}</TableCell>
      <TableCell>{nation.name}</TableCell>
      <TableCell>
        <Chip
          label={nation.isActive === false ? "Inactive" : "Active"}
          color={nation.isActive === false ? "default" : "success"}
          size="small"
        />
      </TableCell>
      <TableCell>
        <IconButton aria-label="edit" onClick={() => openEdit(nation)}>
          <EditIcon />
        </IconButton>

        <IconButton
          aria-label={nation.isActive === false ? "activate" : "deactivate"}
          color={nation.isActive === false ? "default" : "success"}
          onClick={() => onToggleStatusClick(nation)}
          disabled={isStatusPending}
        >
          {nation.isActive === false ? <ToggleOffIcon /> : <ToggleOnIcon />}
        </IconButton>
      </TableCell>
    </TableRow>
  );

  const isoCodeHelper = fieldErrors?.code || "2-letter ISO code, e.g. MX";
  const shortNameHelper = fieldErrors?.shortName || "e.g. MEX";
  const nameHelper =
    fieldErrors?.name ||
    (code.trim().toUpperCase() === "GB"
      ? "Required for GB — e.g. England, Scotland"
      : "Optional — auto-filled from ISO code");

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Add nation
        </Button>
      </Box>

      <CustomTable
        name="worldCupNations"
        getItemsMethod={getWorldCupNationsForTable}
        renderHeader={renderHeader}
        renderRow={renderRow}
        columnCount={7}
        isSearchable
        searchLabel="Search by name, short name, or ISO code"
        queryKeyParts={[sortOrder]}
        queryParams={{ sortOrder }}
        pageResetKey={sortOrder}
      />

      <Dialog
        open={open}
        onClose={() => !isSaving && closeDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {mode === "edit" ? "Edit nation" : "Add nation"}
        </DialogTitle>

        <DialogContent>
          {formError ? (
            <Typography sx={{ mt: 1 }} color="error">
              {formError}
            </Typography>
          ) : null}

          <FormControl
            fullWidth
            required
            sx={{ mt: 2 }}
            error={Boolean(fieldErrors?.group)}
          >
            <InputLabel id="world-cup-nation-group-label">Group</InputLabel>
            <Select
              labelId="world-cup-nation-group-label"
              label="Group"
              value={group}
              displayEmpty
              onChange={(e) => {
                setGroup(e.target.value);
                if (fieldErrors?.group) {
                  setFieldErrors((prev) => ({ ...prev, group: undefined }));
                }
              }}
            >
              <MenuItem value="" disabled>
                <em>Select group</em>
              </MenuItem>
              {GROUP_OPTIONS.map((letter) => (
                <MenuItem key={letter} value={letter}>
                  {letter}
                </MenuItem>
              ))}
            </Select>
            {fieldErrors?.group ? (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, ml: 1.75 }}
              >
                {fieldErrors.group}
              </Typography>
            ) : null}
          </FormControl>

          <Autocomplete
            options={COUNTRY_OPTIONS}
            value={selectedCountry}
            onChange={(_, option) => {
              const nextCode = option?.code ?? "";
              handleIsoCodeChange(nextCode);
              if (nextCode) {
                suggestNameFromIsoCode(nextCode);
              }
            }}
            onInputChange={(_, inputValue, reason) => {
              if (reason === "input") {
                handleIsoCodeChange(
                  inputValue.toUpperCase().slice(0, MAX_ISO_CODE_LENGTH),
                );
              }
            }}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${option.code} — ${option.name}`
            }
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="ISO code"
                error={Boolean(fieldErrors?.code)}
                helperText={isoCodeHelper}
                onBlur={handleIsoCodeBlur}
                inputProps={{
                  ...params.inputProps,
                  maxLength: MAX_ISO_CODE_LENGTH,
                }}
                sx={{ mt: 2 }}
              />
            )}
            disabled={isSaving}
          />

          <TextField
            fullWidth
            required
            label="Short name"
            value={shortName}
            onChange={(e) => {
              setShortName(e.target.value);
              if (fieldErrors?.shortName) {
                setFieldErrors((prev) => ({ ...prev, shortName: undefined }));
              }
            }}
            onBlur={() => setShortName((prev) => prev.toUpperCase())}
            error={Boolean(fieldErrors?.shortName)}
            helperText={shortNameHelper}
            inputProps={{ maxLength: MAX_SHORT_NAME_LENGTH }}
            placeholder="MEX"
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => {
              nameManuallyEditedRef.current = true;
              setName(e.target.value);
              if (fieldErrors?.name) {
                setFieldErrors((prev) => ({ ...prev, name: undefined }));
              }
            }}
            error={Boolean(fieldErrors?.name)}
            helperText={nameHelper}
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            sx={{ mt: 2 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog} disabled={isSaving}>
            Cancel
          </Button>

          <Button onClick={submit} variant="contained" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WorldCupNations;
